import type { CommunityLoaderData } from '@communities/community/domain';
import { communityLoader } from '@communities/community/loaders/communityLoader';
import { CommunityDetailPage } from '@communities/community/pages';
import type { CommunityGroupLoaderData } from '@communities/community-groups/domain';
import { communityGroupLoader } from '@communities/community-groups/loaders/communityGroupLoader';
import { ContentSearchPage } from '@content-search/pages';
import { AuthenticatedRoute } from '@core/components';
import { getCommunityAuditRouteConfig } from '@internal-audit/routeConfig';
import { getCommunityModerationRouteConfig } from '@moderation/routeConfig';
import { ClassificationTestingPage } from '@soteria-ui/src/classification-testing/pages/ClassificationTestingPage';
import type { RouteObject } from 'react-router-dom';

import { CommunityGroupDetailPage, CommunityGroupsPage } from './community-groups/pages';
import { routes } from './routes';

/**
 * Get route configuration for the communities domain
 * @returns Array of route objects for the communities domain
 */
export function getRouteConfig(): RouteObject[] {
  return [
    // Community Groups route
    {
      path: routes.communityGroups.root.substring(1), // Remove leading slash
      element: <AuthenticatedRoute />,
      handle: { breadcrumb: 'Community Groups' },
      children: [
        {
          index: true,
          element: <CommunityGroupsPage />,
        },
        {
          path: ':groupId',
          loader: communityGroupLoader,
          element: <CommunityGroupDetailPage />,
          handle: {
            breadcrumb: (data: CommunityGroupLoaderData) => data?.communityGroup?.name || 'Community Group',
          },
        },
      ],
    },
    // Communities route
    {
      path: routes.communities.root.substring(1), // Remove leading slash
      element: <AuthenticatedRoute />,
      handle: {
        breadcrumb: (data: CommunityLoaderData) => {
          // Extract community group name from nested community data
          const community = data?.community;

          return community?.communityGroup?.name || 'Communities';
        },
      },
      children: [
        {
          path: ':communityId',
          loader: communityLoader,
          handle: {
            breadcrumb: (data: CommunityLoaderData) => data?.community?.name || 'Community',
          },
          children: [
            {
              index: true,
              element: <CommunityDetailPage />,
            },
            {
              path: 'classification-testing',
              element: <ClassificationTestingPage />,
              handle: { breadcrumb: 'Classification Testing' },
            },
            {
              path: 'content-search',
              element: <ContentSearchPage />,
              handle: { breadcrumb: 'Content Search' },
            },
            ...getCommunityAuditRouteConfig(),
            ...getCommunityModerationRouteConfig(),
          ],
        },
      ],
    },
  ];
}
